import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import "./homeRedesign.css";
import "./torii.css";
import { toriiMindsData } from "../../data/home";
import { mediaUrl } from "../../services/assetUrl";

const BASE = process.env.PUBLIC_URL || "";
const ROTATE_MS = 3500;

// 16:9 branded graphics from public/images/home/about_torii.
// Content comes from src/data/home/toriiMinds.json (backend-overridable via the
// Home singleton); `data` prop overrides the bundled default.
const ToriiMinds = ({ data }) => {
  const d = data || toriiMindsData;
  const SHOWCASE = d.showcase || [];
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = SHOWCASE.length;

  const go = useCallback((i) => setActive(((i % n) + n) % n), [n]);

  // On narrower 2-column widths the fanned-out side cards would otherwise reach
  // into the left brand panel. `tight` pulls the fan in (fewer + closer cards)
  // so the two halves never collide before the layout stacks at <=991px.
  const [tight, setTight] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1280px)");
    const on = () => setTight(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  // Advance on a timer that resets whenever the slide or pause state changes.
  // Pausing only happens while hovering the coverflow itself (see tnp__flow),
  // so the cursor resting elsewhere never stalls it; leaving always resumes.
  useEffect(() => {
    if (reduce || paused) return undefined;
    const id = setTimeout(() => setActive((a) => (a + 1) % n), ROTATE_MS);
    return () => clearTimeout(id);
  }, [active, paused, reduce, n]);

  // signed shortest offset of card i from the active one (handles wrap-around)
  const offsetOf = (i) => {
    let off = i - active;
    if (off > n / 2) off -= n;
    if (off < -n / 2) off += n;
    return off;
  };

  return (
    <section className="tnp" aria-labelledby="tnp-title">
      <span className="tnp__grid" aria-hidden="true" />
      <span
        className="tnp__logobg"
        aria-hidden="true"
        style={{ backgroundImage: `url("${mediaUrl(d.logobg)}")` }}
      />

      <div className="tnp__inner container">
        {/* ---- Left: brand panel ---- */}
        <div className="tnp__left">
          <span className="tnp__accent" aria-hidden="true" />
          <div className="tnp__brandrow">
            <span className="tnp__eyebrow">{d.eyebrow}</span>
            <img className="tnp__logo" src={mediaUrl(d.logo)} alt={d.cardTag || "Torii Minds"} />
          </div>
          <h2 id="tnp-title" className="tnp__title">
            {d.title} <span>{d.titleHighlight}</span>
          </h2>
          <p className="tnp__text">{d.text}</p>
          {/* <Link className="tnp__cta" to={(d.cta && d.cta.link) || "/"}>
            <span className="tnp__cta-label">{d.cta && d.cta.label}</span>
            <span className="tnp__cta-arrow" aria-hidden="true">→</span>
          </Link> */}
        </div>

        {/* ---- Right: 3D coverflow ---- */}
        <div className="tnp__flow">
          {/* title bounces up above the active card, fades out on change */}
          <div className="tnp__headbar">
            <AnimatePresence mode="wait">
              <motion.h3
                key={active}
                className="tnp__headline"
                initial={reduce ? false : { opacity: 0, y: 42, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8, transition: { duration: 0.18, ease: "easeIn" } }}
                transition={{
                  y: { type: "spring", stiffness: 480, damping: 13 },
                  scale: { type: "spring", stiffness: 480, damping: 15 },
                  opacity: { duration: 0.26 },
                }}
              >
                {SHOWCASE[active].title}
              </motion.h3>
            </AnimatePresence>
          </div>

          <div className={`tnp__stage${reduce ? " is-flat" : ""}`}>
            {SHOWCASE.map((s, i) => {
              const off = offsetOf(i);
              const abs = Math.abs(off);
              const isActive = i === active;
              const hidden = abs > (tight ? 1 : 2);
              const spreadX = tight ? 32 : 40;
              const depth = tight ? 120 : 150;
              const rot = tight ? 38 : 42;
              const style = {
                transform: `translateX(${off * spreadX}%) translateZ(${-abs * depth}px) rotateY(${off * -rot}deg) scale(${1 - abs * 0.12})`,
                opacity: hidden ? 0 : 1 - abs * 0.22,
                zIndex: 20 - abs,
                pointerEvents: hidden ? "none" : "auto",
              };
              return (
                <div
                  key={s.src}
                  className={`tnp__card${isActive ? " is-active" : ""}`}
                  style={style}
                  onMouseEnter={() => !hidden && setPaused(true)}
                  onMouseLeave={() => setPaused(false)}
                  onClick={() => !isActive && go(i)}
                  role={isActive ? undefined : "button"}
                  tabIndex={isActive || hidden ? -1 : 0}
                  aria-label={isActive ? undefined : `Show ${s.title}`}
                  onKeyDown={(e) => { if (!isActive && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); go(i); } }}
                >
                  <img
                    src={mediaUrl(s.src)}
                    alt={s.title}
                    loading="lazy"
                    style={s.zoom ? { "--zoom": s.zoom, objectPosition: s.focus || "center" } : undefined}
                  />
                  <span className="tnp__card-scrim" aria-hidden="true" />
                  <span className="tnp__card-frame" aria-hidden="true" />
                  <div className="tnp__card-desc">
                    <span className="tnp__card-tag" aria-hidden="true">{d.cardTag}</span>
                    <h4 className="tnp__card-dtitle">{s.title}</h4>
                    <p>{s.caption}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="tnp__dots" role="tablist" aria-label="Torii Minds highlights">
            {SHOWCASE.map((s, i) => (
              <button
                key={s.src}
                type="button"
                role="tab"
                aria-selected={i === active}
                aria-label={s.title}
                className={`tnp__dot${i === active ? " is-active" : ""}`}
                onClick={() => go(i)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ToriiMinds;
